// Product-tour analytics (VRG-267). API client for the append-only event log and
// the internal read that aggregates it.
//
// Types are declared locally rather than imported from `@repo/types`, matching
// `tourProgressApi.ts` and `supportApi.ts` — apps/platform does not depend on that
// package. The canonical definitions live in
// `apps/api/src/features/product-tours/product-tours.types.ts`; keep the two in step.

import { createApiCall } from './index'

/** Mirrors `TourEventType` in the engine and in the API. Five values, one contract. */
export type TourEventType = 'start' | 'step' | 'complete' | 'skip' | 'cta'

export interface TourEventPayload {
  tourKey: string
  stepId: string
  stepIndex: number
  /** CONTENT step count — excludes the engine's appended outro (§48). */
  totalSteps: number
  type: TourEventType
}

/** One row of the cross-tour summary. Counts are DISTINCT USERS, not events. */
export interface TourAnalyticsSummaryRow {
  tourKey: string
  started: number
  completed: number
  skipped: number
  totalSteps: number
}

/** One rung of a funnel. */
export interface TourAnalyticsStepRow {
  tourKey: string
  stepIndex: number
  reached: number
}

export interface TourAnalytics {
  windowStart: string
  windowDays: number
  /** Non-null when `?user=` was supplied. `id: ''` means the address matched nobody. */
  user: { email: string; id: string } | null
  summary: TourAnalyticsSummaryRow[]
  steps: TourAnalyticsStepRow[]
  totalEvents: number
  computedAt: string
}

export const tourAnalyticsApi = {
  /**
   * Append one event for the current user.
   *
   * **Never throws.** A tour is a teaching aid; measuring it must not be able to
   * interrupt it. Every failure — offline, 403, a 500 — resolves quietly, because
   * the alternative is an unhandled rejection surfacing mid-tour from the one
   * subsystem whose entire job is to be invisible. The API mirrors this by
   * returning 202 and no body.
   */
  async record(payload: TourEventPayload): Promise<void> {
    try {
      await createApiCall({ method: 'POST', url: '/tour-events', data: payload })
    } catch {
      // Deliberately swallowed. See above.
    }
  },

  /**
   * The 30-day funnel + summary. Staff-only server-side
   * (`internal_analytics_view`); a caller without it gets a 403, which the hook
   * surfaces as a permission state rather than an empty one — "not allowed to
   * look" and "nobody took the tour" must never render the same way.
   */
  async analytics(userEmail?: string): Promise<TourAnalytics> {
    const query = userEmail ? `?user=${encodeURIComponent(userEmail)}` : ''
    const response = await createApiCall({
      method: 'GET',
      url: `/internal/tour-analytics${query}`,
    })
    // `createApiCall`'s errorHandler RESOLVES with the error response rather than
    // throwing (see services/index.ts), so the status has to be checked here or a
    // 403 body would be rendered as a zeroed funnel.
    if (response.status !== 200) {
      const error = new Error(
        response?.data?.message || 'Failed to load tour analytics',
      ) as Error & { status?: number }
      error.status = response?.status ?? 0
      throw error
    }
    return response.data
  },

  /**
   * The same 30-day funnel + summary, scoped to ONE company (VRG-515/516).
   *
   * Same `TourAnalytics` shape as `analytics` above — deliberately, so the panel
   * that renders a funnel does not care which scope produced it.
   *
   * Two properties of the scope worth knowing before reading a number off it:
   *
   *  - `tour_events.company_id` is NULLABLE, and Vera staff taking a tour carry
   *    `null`. So a company scope EXCLUDES staff runs by construction. That is the
   *    intended reading of "this company's adoption", but it means these figures
   *    will not reconcile with the unscoped page's, and should not be expected to.
   *  - Gated on `internal_analytics_view`, NOT the `internal_support_manage` that
   *    gates the other two company tabs. A staffer can hold one and not the other,
   *    so the tabs can legitimately disagree about what is visible.
   */
  async companyAnalytics(companyId: string): Promise<TourAnalytics> {
    const response = await createApiCall({
      method: 'GET',
      url: `/companies/admin/${companyId}/tour-analytics`,
    })
    if (response.status !== 200) {
      const error = new Error(
        response?.data?.message || 'Failed to load tour analytics',
      ) as Error & { status?: number }
      error.status = response?.status ?? 0
      throw error
    }
    return response.data
  },
}
